import type { Components, PaletteColor, Theme } from '@mui/material/styles'

import { alphaByTheme } from '../../../../utils/colorUtils.ts'
import { mixColors } from '../../../../utils/getColor.ts'
import { getInputBackground } from './inputStyled.ts'

const getPaletteColor = (theme: Theme, color?: string): PaletteColor | undefined => {
  const key = color?.replace(/\.main$/, '')
  if (!key || key === 'inherit' || key === 'default') return undefined
  const palette = theme.palette as unknown as Record<string, PaletteColor | undefined>
  const entry = Object.prototype.hasOwnProperty.call(palette, key) ? palette[key] : undefined
  return entry && typeof entry.main === 'string' ? entry : undefined
}

const getScrollbarStyles = (theme: Omit<Theme, 'components'>) => {
  const colors =
    theme.palette.mode === 'dark'
      ? { thumb: '#737373', hover: '#909090', active: '#A3A3A3' }
      : { thumb: '#8A8A8A', hover: '#6F6F6F', active: '#5B5B5B' }

  return {
    // Conserva el scroll nativo en dispositivos táctiles y en contraste forzado.
    '@media (hover: hover) and (pointer: fine) and (forced-colors: none)': {
      'html, body, body *': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${colors.thumb} transparent`,
      },
      // Las propiedades estándar en auto permiten aplicar los pseudoelementos.
      '@supports selector(::-webkit-scrollbar)': {
        'html, body, body *': {
          scrollbarWidth: 'auto',
          scrollbarColor: 'auto',
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.thumb,
            backgroundClip: 'padding-box',
            border: '2px solid transparent',
            borderRadius: '999px',
            minHeight: '24px',
            minWidth: '24px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: colors.hover,
          },
          '&::-webkit-scrollbar-thumb:active': {
            backgroundColor: colors.active,
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  }
}

/**
 * Estilos para componentes MUI
 * @author isi-template
 */
export const components: Components<Omit<Theme, 'components'>> = {
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.surfaceBorderRadius,
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: ({ theme }) => ({
        borderRadius: theme.shape.surfaceBorderRadius,
      }),
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        '&.Mui-disabled': {
          opacity: 0.6,
        },
      },
      outlined: ({ theme, ownerState }) =>
        ownerState.color === 'inherit'
          ? { '&:not(.Mui-disabled)': { borderColor: theme.palette.divider } }
          : {},
      // Lee el color solicitado; no necesita una lista de nombres ni nuevas ampliaciones.
      contained: ({ theme, ownerState }) => {
        if (ownerState.color === 'inherit') {
          const isDark = theme.palette.mode === 'dark'
          const background = theme.palette.grey[isDark ? 800 : 300]
          return {
            // boxShadow: 'none',
            // '&:hover, &:active, &.Mui-focusVisible': {
            //   boxShadow: 'none',
            // },
            '@media (hover: hover)': {
              '&:hover:not(.Mui-disabled)': {
                '--variant-containedBg': mixColors('#000000', background, 0.08),
              },
            },
          }
        }

        const color = getPaletteColor(theme, ownerState.color)
        if (!color?.buttonBgColor || !color.buttonTextColor || !color.buttonHoverBgColor) {
          return {}
        }

        // Variables de MUI: mantiene intactos los estados disabled y loading.
        return {
          '--variant-containedColor': color.buttonTextColor,
          '--variant-containedBg': color.buttonBgColor,
          '@media (hover: hover)': {
            '&:hover': { '--variant-containedBg': color.buttonHoverBgColor },
          },
        }
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const paletteColor = getPaletteColor(theme, ownerState.color)

        if (paletteColor && paletteColor.softBgColor) {
          if (ownerState.variant === 'filled') {
            return {
              backgroundColor: paletteColor.softBgColor,
              color: paletteColor.softTextColor,
              border: `1px solid ${paletteColor.borderMedium}`,
              fontWeight: 400,
              '&:hover': {
                backgroundColor: paletteColor.softBgColor,
              },
            }
          }

          if (ownerState.variant === 'outlined') {
            return {
              backgroundColor: 'transparent',
              color: paletteColor.textColor,
              border: `1px solid ${paletteColor.borderStrong}`,
              fontWeight: 400,
              '&:hover': {
                backgroundColor: paletteColor.hoverBgColor,
              },
            }
          }
        }
        return {}
      },
    },
  },

  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      '*': { boxSizing: 'border-box' },
      html: {
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased',
        height: '100%',
        width: '100%',
      },
      body: {
        height: '100%',
      },
      ...getScrollbarStyles(theme),
      a: { textDecoration: 'none', color: 'inherit' },
      '#root': { height: '100%' },
      '#nprogress .bar': { zIndex: '2000 !important' },
    }),
  },

  MuiFab: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const color = getPaletteColor(theme, ownerState.color)
        return {
          boxShadow: theme.shadows[12],
          '&:hover': { boxShadow: theme.shadows[14] },
          ...(color?.buttonBgColor
            ? {
                '&:not(.Mui-disabled)': {
                  backgroundColor: color.buttonBgColor,
                  color: color.buttonTextColor,
                },
                '&:not(.Mui-disabled):hover': {
                  backgroundColor: color.buttonHoverBgColor,
                },
              }
            : {}),
        }
      },
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        '&:before': { display: 'none' },
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: { fontSize: '0.6875rem' },
    },
  },

  MuiTextField: {
    defaultProps: {
      size: 'small',
    },
  },

  MuiAutocomplete: {
    defaultProps: {
      size: 'small',
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.input,
        '&.Mui-disabled': {
          backgroundColor: theme.palette.background.inputDisabled,
          '& .MuiOutlinedInput-notchedOutline': {
            borderStyle: 'dashed',
            borderColor: theme.palette.text.disabled,
            borderWidth: '1px',
          },
          '& .MuiInputBase-input': {
            opacity: 1,
            color: theme.palette.text.secondary,
            WebkitTextFillColor: theme.palette.text.secondary,
            fontWeight: 500,
          },
        },
        '&.Mui-readOnly': {
          backgroundColor: theme.palette.background.inputReadOnly,
          cursor: 'default',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '& .MuiInputBase-input': {
            cursor: 'default',
            fontWeight: 400,
          },
        },
      }),
    },
  },

  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        backgroundColor: getInputBackground(theme, ownerState.isFieldDisabled, ownerState.isFieldReadOnly),
      }),
    },
  },

  MuiSelect: {
    defaultProps: {
      size: 'small',
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const color = getPaletteColor(theme, ownerState.color)
        return {
          '& .MuiSvgIcon-root': { fontSize: 24 },
          ...(theme.palette.mode === 'dark' && color?.controlColor
            ? {
                '&.Mui-checked:not(.Mui-disabled), &.MuiCheckbox-indeterminate:not(.Mui-disabled)': {
                  color: color.controlColor,
                },
              }
            : {}),
        }
      },
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const color = getPaletteColor(theme, ownerState.color)
        return {
          '& .MuiSvgIcon-root': { fontSize: 24 },
          ...(theme.palette.mode === 'dark' && color?.controlColor
            ? {
                '&.Mui-checked:not(.Mui-disabled)': { color: color.controlColor },
              }
            : {}),
        }
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const color = getPaletteColor(theme, ownerState.color)
        if (theme.palette.mode !== 'dark' || !color?.controlColor) return {}
        return {
          '& .MuiSwitch-switchBase.Mui-checked:not(.Mui-disabled)': { color: color.controlColor },
          '& .MuiSwitch-switchBase.Mui-checked:not(.Mui-disabled) + .MuiSwitch-track': {
            backgroundColor: color.controlColor,
          },
        }
      },
    },
  },

  MuiSlider: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const color = getPaletteColor(theme, ownerState.color)
        if (theme.palette.mode !== 'dark' || !color?.controlColor) return {}
        return { '&:not(.Mui-disabled)': { color: color.controlColor } }
      },
    },
  },

  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        '& .MuiFormControlLabel-label': {
          fontSize: '0.875rem',
        },
        '&:has(.MuiCheckbox-sizeSmall), &:has(.MuiRadio-sizeSmall)': {
          '& .MuiFormControlLabel-label': {
            fontSize: '0.75rem',
          },
        },
        '&:has(.MuiCheckbox-sizeLarge), &:has(.MuiRadio-sizeLarge)': {
          '& .MuiFormControlLabel-label': {
            fontSize: '1rem',
            fontWeight: 500,
          },
        },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '6px 16px',
      },
      head: {
        fontWeight: 600,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        alignItems: 'flex-start',
        padding: '3px 8px',
        borderRadius: theme.shape.surfaceBorderRadius,
      }),
      icon: {
        padding: '5px 0',
        marginRight: '8px',
        opacity: 0.9,
      },
      message: {
        padding: '6px 0',
        width: '100%',
      },
      action: {
        padding: '2px 0 0 0',
        marginRight: '0px',
      },
      standard: ({ theme, ownerState }) => {
        const colorDef = getPaletteColor(theme, ownerState.color ?? ownerState.severity ?? 'info')
        if (!colorDef || !colorDef.softBgColor) return {}
        return {
          backgroundColor: colorDef.softBgColor,
          color: colorDef.softTextColor,
          border: `1px solid ${colorDef.borderColor}`,
          '& .MuiAlert-icon': {
            color: colorDef.softTextColor,
          },
        }
      },
      filled: () => ({
        color: '#FFFFFF !important',
        fontWeight: 500,
        '& .MuiAlert-icon': {
          color: '#FFFFFF !important',
        },
        '& .MuiAlert-action': {
          color: '#FFFFFF !important',
        },
        '& .MuiAlertTitle-root': {
          color: '#FFFFFF !important',
        },
      }),
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: {
        marginTop: '-1px',
        marginBottom: '0px',
        fontWeight: 600,
        lineHeight: 1.5,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 6,
        '&:hover': {
          backgroundColor: theme.palette.primary.hoverBgColor,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.softBgColor,
          '&:hover': {
            backgroundColor: theme.palette.primary.strongBgColor,
          },
        },
      }),
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 6,
        '&:hover': {
          backgroundColor: theme.palette.primary.hoverBgColor,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.softBgColor,
          '&:hover': {
            backgroundColor: theme.palette.primary.strongBgColor,
          },
          '& .MuiListItemIcon-root': {
            color: theme.palette.primary.main, // Luminoso y legible
          },
          '& .MuiListItemText-primary': {
            color: theme.palette.primary.main, // Luminoso y legible
          },
        },
      }),
    },
  },

  MuiDialog: {
    styleOverrides: {
      backdrop: ({ theme }) => ({
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.65)' : 'rgba(0, 0, 0, 0.35)',
        '@media (prefers-reduced-transparency: reduce)': {
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.6)',
        },
      }),
      paper: ({ theme }) => ({
        backgroundImage: 'none',
        backgroundColor: theme.palette.background.paper,
        borderRadius: (theme.shape as any).surfaceBorderRadius || 12,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 24px 48px -12px rgba(0, 0, 0, 0.75)'
            : '0 20px 40px -15px rgba(0, 0, 0, 0.2)',
        border: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down('sm')]: {
          margin: '8px',
          width: 'calc(100% - 16px)',
          maxWidth: 'calc(100% - 16px) !important',
          maxHeight: 'calc(100% - 16px)',
          '@supports (height: 100dvh)': {
            maxHeight: 'calc(100dvh - 16px)',
          },
        },
        '&.MuiDialog-paperFullScreen': {
          margin: '0 !important',
          width: '100% !important',
          maxWidth: '100% !important',
          height: '100% !important',
          maxHeight: '100% !important',
          borderRadius: '0 !important',
          border: 'none',
          '@supports (height: 100dvh)': {
            height: '100dvh !important',
            maxHeight: '100dvh !important',
          },
        },
      }),
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '16px 24px',
        backgroundColor: alphaByTheme(theme.palette.divider, theme, 0.02, 0.01),
        [theme.breakpoints.down('sm')]: {
          padding: '12px 16px',
          fontSize: '1.1rem',
        },
      }),
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '16px 24px',
        overscrollBehavior: 'contain',
        [theme.breakpoints.down('sm')]: {
          padding: '12px 14px',
        },
      }),
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '12px 24px',
        gap: '8px',
        backgroundColor: alphaByTheme(theme.palette.divider, theme, 0.02, 0.01),
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
          padding: '10px 14px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          flexWrap: 'wrap',
          '& > button, & > .MuiButton-root': {
            flex: '1 1 auto',
            minWidth: '100px',
          },
        },
      }),
    },
  },
}
